import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Dog, Cat, Calendar, Weight, Activity, Edit2, Trash2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { PetProfileBuilder } from '../tools/PetProfileBuilder';
// Mock data
const initialPets = [{
  id: '1',
  name: 'Max',
  type: 'dog',
  breed: 'Golden Retriever',
  age: 3,
  weight: 28,
  activity: 'high',
  image: 'https://images.unsplash.com/photo-1552053831-71594a27632d?auto=format&fit=crop&q=80&w=300'
}, {
  id: '2',
  name: 'Luna',
  type: 'cat',
  breed: 'Siamés',
  age: 2,
  weight: 4,
  activity: 'normal',
  image: 'https://images.unsplash.com/photo-1513245543132-31f507417b26?auto=format&fit=crop&q=80&w=300'
}];
export function PetsSection() {
  const [pets, setPets] = useState(initialPets);
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const handleDelete = (id: string) => {
    setPets(pets.filter(p => p.id !== id));
  };
  const handlePetCreated = (petData: any) => {
    const newPet = {
      id: Date.now().toString(),
      name: petData.name,
      type: petData.type,
      breed: petData.breed || 'Mestizo',
      age: parseInt(petData.age),
      weight: parseFloat(petData.weight),
      activity: petData.activityLevel,
      image: petData.type === 'dog' ? 'https://images.unsplash.com/photo-1543466835-00a7907e9de1?auto=format&fit=crop&q=80&w=300' : 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?auto=format&fit=crop&q=80&w=300'
    };
    setPets([...pets, newPet]);
    setIsBuilderOpen(false);
  };
  if (isBuilderOpen) {
    return <div className="space-y-6" data-id="element-370">
        <div className="flex items-center justify-between mb-6" data-id="element-371">
          <h2 className="text-2xl font-bold font-serif text-charcoal-900" data-id="element-372">
            Añadir Nueva Mascota
          </h2>
          <Button variant="outline" onClick={() => setIsBuilderOpen(false)} data-id="element-373">
            Cancelar
          </Button>
        </div>
        <PetProfileBuilder onComplete={handlePetCreated} data-id="element-374" />
      </div>;
  }
  return <div className="space-y-8" data-id="element-375">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-376">
        <div data-id="element-377">
          <h2 className="text-2xl font-bold font-serif text-charcoal-900 mb-2" data-id="element-378">
            Mis Mascotas
          </h2>
          <p className="text-gray-500" data-id="element-379">
            Gestiona los perfiles de tus compañeros peludos para recibir
            recomendaciones personalizadas.
          </p>
        </div>
        <Button onClick={() => setIsBuilderOpen(true)} className="shadow-lg shadow-primary-900/10" data-id="element-380">
          <Plus className="w-5 h-5 mr-2" data-id="element-381" />
          Añadir Mascota
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-382">
        <AnimatePresence mode="popLayout" data-id="element-383">
          {pets.map(pet => <motion.div key={pet.id} layout initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.9
        }} data-id="element-384">
              <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300 border-gray-100" data-id="element-385">
                <div className="relative h-48 bg-gray-100" data-id="element-386">
                  <img src={pet.image} alt={pet.name} className="w-full h-full object-cover" data-id="element-387" />
                  <div className="absolute top-4 right-4 flex gap-2" data-id="element-388">
                    <button className="p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white text-gray-600 hover:text-primary-600 transition-colors shadow-sm" data-id="element-389">
                      <Edit2 className="w-4 h-4" data-id="element-390" />
                    </button>
                    <button onClick={() => handleDelete(pet.id)} className="p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white text-gray-600 hover:text-red-500 transition-colors shadow-sm" data-id="element-391">
                      <Trash2 className="w-4 h-4" data-id="element-392" />
                    </button>
                  </div>
                  <div className="absolute bottom-4 left-4" data-id="element-393">
                    <Badge variant="primary" className="shadow-md backdrop-blur-md bg-white/90 text-primary-800 border-none" data-id="element-394">
                      {pet.type === 'dog' ? <Dog className="w-3 h-3 mr-1" data-id="element-395" /> : <Cat className="w-3 h-3 mr-1" data-id="element-396" />}
                      {pet.breed}
                    </Badge>
                  </div>
                </div>

                <div className="p-6" data-id="element-397">
                  <div className="flex justify-between items-start mb-4" data-id="element-398">
                    <h3 className="text-xl font-bold text-charcoal-900 font-serif" data-id="element-399">
                      {pet.name}
                    </h3>
                    <Badge variant={pet.activity === 'high' ? 'warning' : 'secondary'} size="sm" data-id="element-400">
                      Actividad{' '}
                      {pet.activity === 'high' ? 'Alta' : pet.activity === 'normal' ? 'Media' : 'Baja'}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-401">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl" data-id="element-402">
                      <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-primary-600 shadow-sm" data-id="element-403">
                        <Calendar className="w-4 h-4" data-id="element-404" />
                      </div>
                      <div data-id="element-405">
                        <p className="text-xs text-gray-500 font-bold uppercase" data-id="element-406">
                          Edad
                        </p>
                        <p className="font-bold text-charcoal-900" data-id="element-407">
                          {pet.age} años
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl" data-id="element-408">
                      <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-blue-600 shadow-sm" data-id="element-409">
                        <Weight className="w-4 h-4" data-id="element-410" />
                      </div>
                      <div data-id="element-411">
                        <p className="text-xs text-gray-500 font-bold uppercase" data-id="element-412">
                          Peso
                        </p>
                        <p className="font-bold text-charcoal-900" data-id="element-413">
                          {pet.weight} kg
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-100" data-id="element-414">
                    <Button variant="outline" className="w-full text-sm" data-id="element-415">
                      Ver Plan Nutricional
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>)}
        </AnimatePresence>

        <motion.button whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} onClick={() => setIsBuilderOpen(true)} className="h-full min-h-[300px] rounded-[2rem] border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-4 text-gray-400 hover:text-primary-600 hover:border-primary-200 hover:bg-primary-50/30 transition-all group" data-id="element-416">
          <div className="w-16 h-16 rounded-full bg-gray-50 group-hover:bg-primary-100 flex items-center justify-center transition-colors" data-id="element-417">
            <Plus className="w-8 h-8" data-id="element-418" />
          </div>
          <span className="font-bold text-lg" data-id="element-419">Añadir otra mascota</span>
        </motion.button>
      </div>
    </div>;
}